"use client";

import { createContext, ReactNode, useContext, useMemo } from "react";

export type AgentScope = {
  conversationId?: string;
  clientId?: string;
  meetingPrepId?: string;
};

// Maps the FE's camelCase scope keys to the snake_case `context` payload the
// agent-service expects. Adding a new scope key = add it to AgentScope above
// and add one line here. Explicit on purpose: TS catches missing keys at
// compile time, and the file is the single source of truth for what crosses
// the wire.
export const toWireContext = (
  scope: AgentScope,
): Record<string, string | string[]> => {
  const out: Record<string, string | string[]> = {};
  if (scope.conversationId) out.conversation_id = scope.conversationId;
  if (scope.clientId) out.client_id = scope.clientId;
  if (scope.meetingPrepId) out.meeting_prep_id = scope.meetingPrepId;
  return out;
};

const AgentScopeContext = createContext<AgentScope>({});

interface AgentScopeProviderProps {
  scope: AgentScope;
  children: ReactNode;
}

export const AgentScopeProvider = ({
  scope,
  children,
}: AgentScopeProviderProps) => {
  const parent = useContext(AgentScopeContext);
  const merged = useMemo(() => ({ ...parent, ...scope }), [parent, scope]);
  return (
    <AgentScopeContext.Provider value={merged}>
      {children}
    </AgentScopeContext.Provider>
  );
};

export const useAgentScope = (): AgentScope => useContext(AgentScopeContext);
